/**
 * Fee Calculator Utilities
 * 
 * Centralized fee calculation logic for all artwork methods.
 * All fee rates come from backend configuration (imprint_data).
 * Default values are fallbacks only.
 */

/**
 * Calculate setup fee based on number of colors and artwork method
 * 
 * Rules:
 * - Start New Imprint (COMDA): setupFeeRate per unique color
 * - Start New Imprint (NON-COMDA): setupFeeRate per additional color (first color free)
 * - Send Us Artwork: setupFeeRate flat fee
 * - Use Previous Artwork: $0 (already paid)
 * 
 * @param {number} uniqueColorsCount - Number of unique colors
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {boolean} isComda - Whether this is a Comda store
 * @param {number} setupFeeRate - Setup fee rate from backend (imprint_data.setup_fee)
 * @returns {number} Setup fee amount
 */
export function calculateSetupFee(uniqueColorsCount, artworkMethod = 'new', isComda = true, setupFeeRate = 30, isFirstImprintLocation = true) {
    return calculateSetupFeeDetailed(uniqueColorsCount, artworkMethod, isComda, setupFeeRate, isFirstImprintLocation).total;
}

export function calculateSetupFeeDetailed(uniqueColorsCount, artworkMethod = 'new', isComda = true, setupFeeRate = 30, isFirstImprintLocation = true) {
    if (artworkMethod === 'send') {
        const qty = isComda ? 1 : 0;
        return { qty, unitPrice: setupFeeRate, total: qty * setupFeeRate };
    }

    if (isComda) {
        if (uniqueColorsCount === 0) {
            return { qty: 1, unitPrice: setupFeeRate, total: setupFeeRate };
        }
        return { qty: uniqueColorsCount, unitPrice: setupFeeRate, total: uniqueColorsCount * setupFeeRate };
    } else {
        if (isFirstImprintLocation) {
            if (uniqueColorsCount <= 1) {
                return { qty: 0, unitPrice: setupFeeRate, total: 0 };
            }
            const qty = uniqueColorsCount - 1;
            return { qty, unitPrice: setupFeeRate, total: qty * setupFeeRate };
        } else {
            if (uniqueColorsCount === 0) {
                return { qty: 1, unitPrice: setupFeeRate, total: setupFeeRate };
            }
            return { qty: uniqueColorsCount, unitPrice: setupFeeRate, total: uniqueColorsCount * setupFeeRate };
        }
    }
}

/**
 * Calculate running fee based on colors and quantity
 * 
 * Rules:
 * - Only for Start New Imprint
 * - Formula: (colors - 1) * runningFeeRate * quantity
 * - First color is free, additional colors cost runningFeeRate per item
 * 
 * @param {number} uniqueColorsCount - Number of unique colors
 * @param {number} totalQuantity - Total items quantity
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {number} runningFeeRate - Running fee per item from backend (imprint_data.running_fee)
 * @returns {number} Running fee amount
 */
export function calculateRunningFee(uniqueColorsCount, totalQuantity, artworkMethod = 'new', runningFeeRate = 0) {
    console.log('[calculateRunningFee] Input:', {
        uniqueColorsCount,
        totalQuantity,
        artworkMethod,
        runningFeeRate
    });
    
    if (artworkMethod !== 'new') {
        console.log('[calculateRunningFee] Returning 0: artworkMethod is not new');
        return 0; // No running fee for send/previous
    }
    
    if (uniqueColorsCount <= 1) {
        console.log('[calculateRunningFee] Returning 0: only 1 or 0 colors');
        return 0; // No running fee for 1 color
    }
    
    const additionalColors = uniqueColorsCount - 1;
    return additionalColors * runningFeeRate * totalQuantity;
}

/**
 * Calculate custom color fee (Pantone)
 * 
 * Rules:
 * - customColorFeeRate PER UNIQUE custom/Pantone color
 * - Only for Start New Imprint
 * 
 * @param {boolean|number} hasPantoneColorOrCount - Whether Pantone color is used (boolean) OR count of unique custom colors (number)
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {number} customColorFeeRate - Custom color fee from backend (imprint_data.custom_colour_fee)
 * @returns {number} Custom color fee amount
 */
export function calculateCustomColorFee(hasPantoneColorOrCount, artworkMethod = 'new', customColorFeeRate = 50) {
    return calculateCustomColorFeeDetailed(hasPantoneColorOrCount, artworkMethod, customColorFeeRate).total;
}

export function calculateCustomColorFeeDetailed(hasPantoneColorOrCount, artworkMethod = 'new', customColorFeeRate = 50) {
    if (artworkMethod !== 'new') {
        return { qty: 0, unitPrice: customColorFeeRate, total: 0 };
    }

    let customColorCount = 0;
    if (typeof hasPantoneColorOrCount === 'number') {
        customColorCount = hasPantoneColorOrCount;
    } else if (hasPantoneColorOrCount === true) {
        customColorCount = 1;
    }

    return { qty: customColorCount, unitPrice: customColorFeeRate, total: customColorCount * customColorFeeRate };
}

/**
 * Calculate image upload fee
 * 
 * Rules:
 * - imageFeeRate if image is uploaded
 * - Only for Start New Imprint with image
 * 
 * @param {boolean} hasImage - Whether image is uploaded
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {number} imageFeeRate - Image fee from backend (imprint_data.image_fee)
 * @returns {number} Image fee amount
 */
export function calculateImageFee(hasImage, artworkMethod = 'new', imageFeeRate = 25) {
    if (artworkMethod !== 'new') {
        return 0; // No image fee for send/previous
    }
    
    return hasImage ? imageFeeRate : 0;
}

/**
 * Calculate proof fee
 * 
 * Rules:
 * - proofFeeRate for new/send methods
 * - $0 for previous artwork (proof already approved)
 * 
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {number} proofFeeRate - Proof fee from backend (imprint_data.proof_fee)
 * @returns {number} Proof fee amount
 */
export function calculateProofFee(artworkMethod = 'new', proofFeeRate = 5) {
    if (artworkMethod === 'previous') {
        // return 0; // No proof fee for previous artwork
    }
    
    return proofFeeRate;
}

/**
 * Calculate running fee per item (for cart properties)
 * 
 * Rules:
 * - Only for Start New Imprint
 * - Formula: (colors - 1) * runningFeeRate
 * 
 * @param {number} uniqueColorsCount - Number of unique colors
 * @param {string} artworkMethod - 'new', 'send', or 'previous'
 * @param {number} runningFeeRate - Running fee per item from backend (imprint_data.running_fee)
 * @returns {number} Running fee per item
 */
export function calculateRunningFeePerItem(uniqueColorsCount, artworkMethod = 'new', runningFeeRate = 0) {
    if (artworkMethod !== 'new') {
        return 0;
    }
    
    if (uniqueColorsCount <= 1) {
        return 0;
    }
    
    // return (uniqueColorsCount - 1) * runningFeeRate;
    return runningFeeRate;
}

/**
 * Calculate all fees for an imprint
 * 
 * @param {Object} params - Fee calculation parameters
 * @param {number} params.uniqueColorsCount - Number of unique colors
 * @param {number} params.totalQuantity - Total items quantity
 * @param {boolean} params.hasPantoneColor - Has Pantone/custom color
 * @param {boolean} params.hasImage - Has uploaded image
 * @param {string} params.artworkMethod - 'new', 'send', or 'previous'
 * @param {boolean} params.isComda - Is Comda store
 * @param {Object} params.backendConfig - Backend fee configuration (from imprint_data)
 * @param {number} params.backendConfig.setup_fee - Setup fee rate
 * @param {number} params.backendConfig.running_fee - Running fee rate
 * @param {number} params.backendConfig.custom_colour_fee - Custom color fee rate
 * @param {number} params.backendConfig.image_fee - Image fee rate
 * @param {number} params.backendConfig.proof_fee - Proof fee rate
 * @returns {Object} All calculated fees
 */
export function calculateAllFees({
    uniqueColorsCount = 0,
    totalQuantity = 0,
    hasPantoneColor = false,
    hasImage = false,
    artworkMethod = 'new',
    isComda = true,
    backendConfig = {},
    isFirstImprintLocation = true
}) {
    // Extract rates from backend config with fallbacks
    const setupFeeRate = backendConfig.setup_fee ?? 30;
    const runningFeeRate = backendConfig.running_fee ?? 0;
    const customColorFeeRate = backendConfig.custom_colour_fee ?? 50;
    const imageFeeRate = backendConfig.image_fee ?? 25;
    const proofFeeRate = backendConfig.proof_fee ?? 5;
    
    const fees = {
        setup_fee: calculateSetupFee(uniqueColorsCount, artworkMethod, isComda, setupFeeRate, isFirstImprintLocation),
        running_fee: calculateRunningFee(uniqueColorsCount, totalQuantity, artworkMethod, runningFeeRate),
        customColourFee: calculateCustomColorFee(hasPantoneColor, artworkMethod, customColorFeeRate),
        image_fee: calculateImageFee(hasImage, artworkMethod, imageFeeRate),
        proof_fee: calculateProofFee(artworkMethod, proofFeeRate),
        runningFeePerItem: calculateRunningFeePerItem(uniqueColorsCount, artworkMethod, runningFeeRate)
    };

    const setupFeeDetail = calculateSetupFeeDetailed(uniqueColorsCount, artworkMethod, isComda, setupFeeRate, isFirstImprintLocation);
    const customColorFeeDetail = calculateCustomColorFeeDetailed(hasPantoneColor, artworkMethod, customColorFeeRate);

    fees.setup_fee_qty = setupFeeDetail.qty;
    fees.setup_fee_unit_price = setupFeeDetail.unitPrice;
    fees.customColourFee_qty = customColorFeeDetail.qty;
    fees.customColourFee_unit_price = customColorFeeDetail.unitPrice;
    fees.running_fee_multiplier = uniqueColorsCount > 1 ? uniqueColorsCount - 1 : 0;
    fees.running_fee_rate = runningFeeRate;
    fees.proof_fee_unit_price = proofFeeRate;
    fees.image_fee_qty = hasImage ? 1 : 0;
    fees.image_fee_unit_price = imageFeeRate;

    // Calculate total
    fees.total = fees.setup_fee + fees.running_fee + fees.customColourFee + fees.image_fee + fees.proof_fee;
    
    return fees;
}

/**
 * Get unique colors count from imprint data
 * 
 * @param {Object} imprintData - Imprint data from store
 * @returns {number} Unique colors count
 */
export function getUniqueColorsCount(imprintData) {
    if (!imprintData) return 0;

    const uniqueColors = new Set();
    const messageType = imprintData.messageType || 'text';

    // Add text line colors ONLY if message type allows text
    if (messageType === 'text' || messageType === 'text_image') {
        if (Array.isArray(imprintData.textLines)) {
            imprintData.textLines.forEach(line => {
                // Only count message lines (not image lines) for text colors
                if (!line || line.type === 'image') return;

                // IMPORTANT: count only "used" lines that will be included in payload/cart
                const hasText = typeof line.message === 'string' && line.message.trim().length > 0;
                if (!hasText) return;

                if (line.selectedColor) {
                    uniqueColors.add(line.selectedColor);
                } else if (typeof line.customColor === 'string' && line.customColor.trim().length > 0) {
                    // Backward compatibility: old code could store a color value in customColor
                    uniqueColors.add(line.customColor.trim());
                }
            });
        }
    }

    // Image color from image line in textLines (new architecture)
    if (messageType === 'image' || messageType === 'text_image') {
        if (Array.isArray(imprintData.textLines)) {
            imprintData.textLines.forEach(line => {
                // Only count image lines for image color
                if (!line || line.type !== 'image') return;

                console.log('[getUniqueColorsCount] Image line found:', line);

                // Check selectedColor first (file_name like "black.png")
                if (line.selectedColor) {
                    uniqueColors.add(line.selectedColor);
                    console.log('[getUniqueColorsCount] Added image color from selectedColor:', line.selectedColor);
                }
                // Also check selectedColorObj.file_name (alternative storage)
                else if (line.selectedColorObj?.file_name) {
                    uniqueColors.add(line.selectedColorObj.file_name);
                    console.log('[getUniqueColorsCount] Added image color from selectedColorObj.file_name:', line.selectedColorObj.file_name);
                }
            });
        }
        
        // Legacy: Image color from uploadedImage/imageData (old architecture)
        if (imprintData.uploadedImage?.selectedColor) {
            uniqueColors.add(imprintData.uploadedImage.selectedColor);
        }
        if (imprintData.imageData?.selectedColor) {
            uniqueColors.add(imprintData.imageData.selectedColor);
        }
    }

    console.log('[getUniqueColorsCount] Final unique colors:', uniqueColors.size, Array.from(uniqueColors));
    return uniqueColors.size;
}

/**
 * Check if imprint has Pantone/custom color
 * 
 * @param {Object} imprintData - Imprint data from store
 * @returns {boolean} True if has Pantone color
 */
export function hasPantoneColor(imprintData) {
    if (!imprintData || !imprintData.textLines) return false;
    
    const messageType = imprintData.messageType || 'text';

    // Only check for Pantone if message type allows text
    if (messageType !== 'text' && messageType !== 'text_image') {
        return false;
    }

    return imprintData.textLines.some(line => {
        // Only check 'message' type lines (not 'image' type)
        if (line.type === 'image') return false;

        // customColor is a boolean: true = Pantone/custom, false = standard color
        // selectedColor contains the actual color name/value
        return line.customColor === true && line.selectedColor;
    });
}

/**
 * Get count of UNIQUE custom/Pantone colors in a single imprint
 * 
 * Each unique custom color should be charged separately.
 * For example, if a location has 2 different Pantone colors,
 * the fee should be customColorFeeRate * 2.
 * 
 * @param {Object} imprintData - Imprint data from store
 * @returns {number} Count of unique custom/Pantone colors
 */
export function getUniqueCustomColorsCount(imprintData) {
    if (!imprintData || !imprintData.textLines) return 0;
    
    const messageType = imprintData.messageType || 'text';

    // Only check for Pantone if message type allows text
    if (messageType !== 'text' && messageType !== 'text_image') {
        return 0;
    }

    const uniqueCustomColors = new Set();

    imprintData.textLines.forEach(line => {
        // Only check 'message' type lines (not 'image' type)
        if (!line || line.type === 'image') return;

        // Only count lines with actual text content
        const hasText = typeof line.message === 'string' && line.message.trim().length > 0;
        if (!hasText) return;

        // customColor is a boolean: true = Pantone/custom, false = standard color
        // selectedColor contains the actual color name/value
        if (line.customColor === true && line.selectedColor) {
            // Use selectedColor as the unique identifier
            // This ensures that the same Pantone color used on multiple lines
            // is only counted once per imprint location
            uniqueCustomColors.add(line.selectedColor);
            console.log('[getUniqueCustomColorsCount] Found custom color:', line.selectedColor);
        }
    });

    console.log('[getUniqueCustomColorsCount] Total unique custom colors:', uniqueCustomColors.size, Array.from(uniqueCustomColors));
    return uniqueCustomColors.size;
}

/**
 * Check if imprint has uploaded image
 * 
 * @param {Object} imprintData - Imprint data from store
 * @returns {boolean} True if has image
 */
export function hasUploadedImage(imprintData) {
    if (!imprintData) return false;

    // Previous format: imprintData.uploadedImage
    return !! imprintData.imageData && imprintData.imageData.url;
}
// ─────────────────────────────────────────────────────────────────────────────
// FEE COMBINERS
// Each function accepts an array of per-imprint fee objects and the fee config,
// and returns the correct combined total based on fee_unit and store type.
// Used by BOTH ReviewSection.vue and payloadBuilders.js to guarantee identical results.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Combine proof fees across all imprint locations.
 *
 * Rules:
 * - 'per order'    → take value from the FIRST location that has it (do NOT sum)
 * - 'per location' → sum across all locations
 *
 * @param {Array<{proof_fee: number}>} imprintsFees - Array of fee objects, one per imprint
 * @param {string} feeUnit - 'per order' | 'per location' (from product fees array fee_unit)
 * @returns {number} Combined proof fee
 */
export function combineProofFee(imprintsFees, feeUnit = 'per order') {
    if (feeUnit === 'per location') {
        return imprintsFees.reduce((sum, fee) => sum + (fee.proof_fee || 0), 0);
    }
    // 'per order' — take from the first imprint that has a non-zero value
    return imprintsFees.reduce((found, fee) => {
        if (found > 0) return found;
        return fee.proof_fee || 0;
    }, 0);
}

/**
 * Get fee_unit for a specific fee_type from the product fees array.
 *
 * @param {Array} feesArray - Product fees array (configurable_array[sku].fees)
 * @param {string} feeType - e.g. 'PROOF', 'SETUP'
 * @param {string} defaultUnit - Fallback if not found
 * @returns {string} fee_unit value
 */
export function getFeeUnit(feesArray, feeType, defaultUnit = 'per order') {
    if (!Array.isArray(feesArray)) return defaultUnit;
    const entry = feesArray.find(f => f.fee_type === feeType);
    return entry?.fee_unit || defaultUnit;
}

/**
 * Get unique colors count across ALL imprint locations (global)
 *
 * This is used for global fee calculation when fees depend on the total number
 * of unique colors across multiple imprint locations.
 *
 * @param {Array<Object>} imprints - Array of imprint objects from newImprintStore
 * @returns {number} Unique colors count across all imprints
 */
export function getGlobalUniqueColorsCount(imprints = []) {
    if (!Array.isArray(imprints) || imprints.length === 0) return 0;

    const uniqueColors = new Set();

    console.log('[getGlobalUniqueColorsCount] Processing', imprints.length, 'imprints');

    imprints.forEach((imprintData, index) => {
        if (!imprintData) return;

        const messageType = imprintData.messageType || 'text';
        console.log(`[getGlobalUniqueColorsCount] Imprint ${index}: messageType=${messageType}`);

        // Text line colors (message lines only) if message type allows text
        if (messageType === 'text' || messageType === 'text_image') {
            if (Array.isArray(imprintData.textLines)) {
                imprintData.textLines.forEach(line => {
                    // Only count message lines (not image lines) for text colors
                    if (!line || line.type === 'image') return;

                    // IMPORTANT: count only "used" lines that will be included in payload/cart
                    const hasText = typeof line.message === 'string' && line.message.trim().length > 0;
                    if (!hasText) return;

                    if (line.selectedColor) {
                        uniqueColors.add(line.selectedColor);
                        console.log(`[getGlobalUniqueColorsCount] Imprint ${index}: Added text color:`, line.selectedColor);
                    } else if (typeof line.customColor === 'string' && line.customColor.trim().length > 0) {
                         uniqueColors.add(line.customColor.trim());
                    }
                });
            }
        }

        // Image color from image line in textLines (new architecture)
        if (messageType === 'image' || messageType === 'text_image') {
            if (Array.isArray(imprintData.textLines)) {
                imprintData.textLines.forEach(line => {
                    // Only count image lines for image color
                    if (!line || line.type !== 'image') return;

                    console.log(`[getGlobalUniqueColorsCount] Imprint ${index}: Image line found:`, line);

                    // Check selectedColor first (file_name like "black.png")
                    if (line.selectedColor) {
                        uniqueColors.add(line.selectedColor);
                        console.log(`[getGlobalUniqueColorsCount] Imprint ${index}: Added image color from selectedColor:`, line.selectedColor);
                    }
                    // Also check selectedColorObj.file_name (alternative storage)
                    else if (line.selectedColorObj?.file_name) {
                        uniqueColors.add(line.selectedColorObj.file_name);
                        console.log(`[getGlobalUniqueColorsCount] Imprint ${index}: Added image color from selectedColorObj.file_name:`, line.selectedColorObj.file_name);
                    }
                });
            }
            
            // Legacy: Image color from uploadedImage/imageData (old architecture)
            if (imprintData.uploadedImage?.selectedColor) {
                uniqueColors.add(imprintData.uploadedImage.selectedColor);
            }
            if (imprintData.imageData?.selectedColor) {
                uniqueColors.add(imprintData.imageData.selectedColor);
            }
        }
    });

    console.log('[getGlobalUniqueColorsCount] Final unique colors:', uniqueColors.size, Array.from(uniqueColors));
    return uniqueColors.size;
}

/**
 * Check if ANY imprint location uses Pantone/custom color (global)
 *
 * @param {Array<Object>} imprints - Array of imprint objects from newImprintStore
 * @returns {boolean} True if at least one imprint uses Pantone/custom color
 */
export function hasPantoneColorGlobal(imprints = []) {
    if (!Array.isArray(imprints) || imprints.length === 0) return false;
    return imprints.some(imprintData => hasPantoneColor(imprintData));
}

/**
 * Check if ANY imprint location has an uploaded image (global)
 *
 * NOTE: We additionally ensure messageType allows images (image/text_image).
 *
 * @param {Array<Object>} imprints - Array of imprint objects from newImprintStore
 * @returns {boolean} True if at least one imprint has image
 */
export function hasUploadedImageGlobal(imprints = []) {
    if (!Array.isArray(imprints) || imprints.length === 0) return false;

    return imprints.some(imprintData => {
        if (!imprintData) return false;

        const messageType = imprintData.messageType || 'text';
        const allowsImage = messageType === 'image' || messageType === 'text_image';

        return allowsImage && hasUploadedImage(imprintData);
    });
}

/**
 * Calculate all fees using GLOBAL aggregation across all imprint locations.
 *
 * This keeps the same fee logic, but replaces:
 * - uniqueColorsCount -> global unique colors across all locations
 * - hasPantoneColor -> true if ANY location uses Pantone/custom color
 *
 * @param {Object} params - Fee calculation parameters
 * @param {Array<Object>} params.imprints - Array of imprint objects from newImprintStore
 * @param {number} params.totalQuantity - Total items quantity
 * @param {string} params.artworkMethod - 'new', 'send', or 'previous'
 * @param {boolean} params.isComda - Is Comda store
 * @param {Object} params.backendConfig - Backend fee configuration (from imprint_data)
 * @returns {Object} All calculated fees
 */
export function calculateAllFeesGlobal({
                                           imprints = [],
                                           totalQuantity = 0,
                                           artworkMethod = 'new',
                                           isComda = true,
                                           backendConfig = {}
                                       }) {
    // For send/previous keep behavior consistent (no need to scan imprints)
    if (artworkMethod !== 'new') {
        return calculateAllFees({
            uniqueColorsCount: artworkMethod === 'send' ? 1 : 0,
            totalQuantity,
            hasPantoneColor: false,
            hasImage: false,
            artworkMethod,
            isComda,
            backendConfig
        });
    }

    const uniqueColorsCount = getGlobalUniqueColorsCount(imprints);
    const hasPantone = hasPantoneColorGlobal(imprints);

    // Image fee isn't explicitly requested to be global, but this is safer if user uploads an image
    // in any location and expects the fee to reflect it.
    const hasImage = hasUploadedImageGlobal(imprints);

    return calculateAllFees({
        uniqueColorsCount,
        totalQuantity,
        hasPantoneColor: hasPantone,
        hasImage,
        artworkMethod,
        isComda,
        backendConfig
    });
}